import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, XIcon } from 'lucide-react';
import { PromptBox } from '../demandes/PromptBox';
import type { ShellTurn } from '../../types/atna';
interface AtnaDialogShellProps {
  /** Shared layout id: the launcher pill morphs into this panel. */
  layoutId: string;
  /** Header subtitle, for example “Report a problem”. */
  subtitle: string;
  onClose: () => void;
  turns: ShellTurn[];
  thinking: boolean;
  /** Id of ATNA's latest turn: only that one keeps its actions. */
  liveAtnaId?: string;
  renderAtna: (kind: string, live: boolean) => React.ReactNode;
  prompt: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    placeholder: string;
    onAddPhoto?: () => void;
    photoAttached?: boolean;
  };
  footnote: string;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function AtnaDialogShell({
  layoutId,
  subtitle,
  onClose,
  turns,
  thinking,
  liveAtnaId,
  renderAtna,
  prompt,
  footnote
}: AtnaDialogShellProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: turns.length > 1 ? 'smooth' : 'auto'
    });
  }, [turns.length, thinking]);
  return <div className="fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center sm:p-6" data-id="element-257" data-mp="2ct3z1bzascz3">
      <motion.button type="button" aria-label="Close the conversation" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} className="absolute inset-0 h-full w-full cursor-default bg-ville-navy/40 focus:outline-none" data-id="element-258" data-mp="282x8h6l86ie7" />

      <motion.div layoutId={layoutId} role="dialog" aria-modal="true" aria-label={`Conversation with ATNA — ${subtitle}`} transition={{
      duration: 0.28,
      ease
    }} style={{
      borderRadius: 28
    }} className="relative flex h-[86vh] w-full max-w-[760px] flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl shadow-ville-navy/20 sm:h-[min(720px,calc(100vh-96px))]" data-id="element-259" data-mp="3sk6i4z5cru0a">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease,
        delay: 0.12
      }} className="flex min-h-0 flex-1 flex-col" data-id="element-260" data-mp="39n57oyt151oy">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6" data-id="element-261" data-mp="1ctaa5vpfl0ys">
            <div className="flex items-center gap-3" data-id="element-262" data-mp="1myjutkib6xi3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-263" data-mp="1hye01vgqsuvs">
                <SparklesIcon className="h-4 w-4" data-id="element-264" data-mp="1vq2e44ajxtfe" />
              </span>
              <div data-id="element-265" data-mp="1qzc60jaqrch0">
                <p className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-266" data-mp="1qyu00uya1jvw">ATNA</p>
                <p className="text-xs text-slate-500" data-id="element-267" data-mp="25n6zw30hjmvo">{subtitle}</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close the conversation" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-268" data-mp="20pom9zbiwjhc">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-269" data-mp="kb5iucnry7zl" />
            </button>
          </header>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6" data-id="element-270" data-mp="gamq8ge24xwc">
            <ol role="log" aria-live="polite" aria-label="Messages" className="space-y-4" data-id="element-271" data-mp="hsbm18rq7crg">
              {turns.map(turn => turn.role === 'user' ? <motion.li key={turn.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease
            }} className="flex justify-end" data-id="element-272" data-mp="qr5fiq9rfn0s">
                    <div className="max-w-[85%] rounded-3xl rounded-br-lg bg-ville-navy px-5 py-3 text-[15px] font-medium leading-relaxed text-white sm:max-w-[75%]" data-id="element-273" data-mp="31b5eggtsck8">
                      {turn.text}
                      {turn.withPhoto && <span className="mt-2 block text-xs font-semibold text-white/60" data-id="element-274" data-mp="cre5f0md6qs2">Photo attached</span>}
                    </div>
                  </motion.li> : <motion.li key={turn.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.24,
              ease
            }} className="flex items-start gap-3" data-id="element-275" data-mp="3bguv55iafcuj">
                    <span className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-276" data-mp="33yevmm41ejru">
                      <SparklesIcon className="h-4 w-4" data-id="element-277" data-mp="2uxoz1g6ycpd2" />
                    </span>
                    <div className="min-w-0 flex-1 rounded-3xl rounded-bl-lg border border-slate-200 bg-white px-5 py-4" data-id="element-278" data-mp="2pycfb4bdnyep">
                      {renderAtna(turn.kind, turn.id === liveAtnaId && !thinking)}
                    </div>
                  </motion.li>)}

              {thinking && <li className="flex items-center gap-3" data-id="element-279" data-mp="2g5ohq7ckpqex">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-280" data-mp="2ba95tky4m4l5">
                    <SparklesIcon className="h-4 w-4" data-id="element-281" data-mp="2289o0qfaewh5" />
                  </span>
                  <span className="sr-only" data-id="element-282" data-mp="22acmzzyf3zy0">ATNA is writing a reply</span>
                  <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-3" data-id="element-283" data-mp="1sj2r7x8gm2ab">
                    {[0, 1, 2].map(dot => <motion.span key={dot} className="h-2 w-2 rounded-full bg-slate-300" animate={{
                  opacity: [0.3, 1, 0.3]
                }} transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  delay: dot * 0.15,
                  ease: 'linear'
                }} data-id="element-284" data-mp="1img8pf3lvnbg" />)}
                  </span>
                </li>}
            </ol>
          </div>

          <div className="shrink-0 border-t border-slate-100 px-4 pb-4 pt-3 sm:px-5" data-id="element-285" data-mp="21llps557psqr">
            <PromptBox value={prompt.value} onChange={prompt.onChange} onSubmit={prompt.onSubmit} onAddPhoto={prompt.onAddPhoto} variant="chat" autoFocus placeholder={prompt.placeholder} busy={thinking} photoAttached={prompt.photoAttached} data-id="element-286" data-mp="26kf5m47z1vu3" />
            <p className="mt-2 text-center text-xs text-slate-400" data-id="element-287" data-mp="2z1rhh4ar7awo">{footnote}</p>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}